import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, ArrowRight, X, Check, SkipForward, ClipboardCheck, Mic } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from '@/components/DictableTextarea';

const ORDINE = ['conley', 'braden', 'barthel', 'mna_sf'];

export default function GiroScale() {
  const navigate = useNavigate();
  const [defs, setDefs] = useState(null);
  const [coda, setCoda] = useState([]);
  const [indice, setIndice] = useState(0);
  const [risposte, setRisposte] = useState({});
  const [note, setNote] = useState('');
  const [notaDiario, setNotaDiario] = useState('');
  const [fatti, setFatti] = useState(0);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/scale/definizioni'), api.get('/scale/riepilogo')]).then(([d, r]) => {
      setDefs(d.data.scale);
      const lista = [];
      // prima gli ospiti senza alcuna scala, poi quelli con scale scadute
      const ospiti = [...r.data.ospiti].sort((a, b) => {
        const mancA = ORDINE.filter(t => a.scale[t].mancante).length;
        const mancB = ORDINE.filter(t => b.scale[t].mancante).length;
        return mancB - mancA || a.ospite_nome.localeCompare(b.ospite_nome);
      });
      ospiti.forEach(o => ORDINE.forEach(tipo => {
        const s = o.scale[tipo];
        if (s.mancante || s.scaduta) {
          lista.push({ ospite_id: o.ospite_id, ospite_nome: o.ospite_nome, stanza: o.stanza, letto: o.letto, area: o.area, tipo, stato: s.mancante ? 'mai compilata' : 'scaduta' });
        }
      }));
      setCoda(lista);
    }).catch(() => toast.error('Impossibile caricare il giro scale'));
  }, []);

  const corrente = coda[indice];
  const definizione = corrente && defs ? defs[corrente.tipo] : null;
  const punteggioParziale = useMemo(
    () => Object.values(risposte).reduce((a, b) => a + b, 0), [risposte]);

  const vai = (delta) => { setIndice(i => Math.min(Math.max(i + delta, 0), coda.length - 1)); setRisposte({}); setNote(''); setNotaDiario(''); };

  const salva = async () => {
    const mancante = definizione.items.find(i => risposte[i.id] === undefined);
    if (mancante) return toast.error(`Rispondi a: ${mancante.testo}`);
    setSalvando(true);
    try {
      const r = await api.post(`/scale/ospite/${corrente.ospite_id}`, { tipo: corrente.tipo, risposte, note });
      if (notaDiario.trim()) {
        await api.post('/diario', {
          ospite_id: corrente.ospite_id, data: new Date().toISOString().slice(0, 10),
          turno: new Date().getHours() < 14 ? 'mattino' : new Date().getHours() < 21 ? 'pomeriggio' : 'notte',
          contenuto: notaDiario.trim(), tags: ['giro_scale'],
        });
      }
      toast.success(`${corrente.ospite_nome}: ${r.data.punteggio}/${r.data.punteggio_max} - ${r.data.classe}`);
      setFatti(f => f + 1);
      if (indice >= coda.length - 1) { setIndice(coda.length); } else { vai(1); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  if (!defs) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand" /></div>;

  if (coda.length === 0 || indice >= coda.length) {
    return (
      <div className="max-w-lg mx-auto py-20 text-center" data-testid="giro-scale-completato">
        <ClipboardCheck className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 font-[Cinzel]">Giro completato</h1>
        <p className="text-sm text-slate-500 mt-2">
          {fatti > 0 ? `${fatti} valutazioni registrate in questo giro.` : 'Nessuna scala da compilare: tutte le valutazioni sono aggiornate.'}
        </p>
        <Button className="mt-6 bg-brand hover:bg-brand-dark text-white" onClick={() => navigate('/scale-alert')} data-testid="torna-scale-btn">
          Torna a Scale e Alert
        </Button>
      </div>
    );
  }

  const progresso = Math.round((indice / coda.length) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-slate-50 overflow-y-auto" data-testid="giro-scale-page">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-8 py-3">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-slate-400">Giro scale · {indice + 1} di {coda.length}</p>
              <h1 className="text-lg font-bold text-slate-900 truncate">{corrente.ospite_nome}</h1>
              <p className="text-[11px] text-slate-400">Camera {corrente.stanza || '-'}/{corrente.letto || '-'} · {corrente.area || 'N/D'}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid="giro-fatti">{fatti} salvate</Badge>
              <Button variant="ghost" size="sm" onClick={() => navigate('/scale-alert')} data-testid="chiudi-giro-btn"><X className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full mt-3 overflow-hidden">
            <div className="h-full bg-brand transition-all duration-500" style={{ width: `${progresso}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base md:text-lg font-semibold text-slate-800">{definizione.nome}</h2>
          <Badge variant="outline" className="text-[10px]">{definizione.ambito}</Badge>
          <Badge className="text-[10px] bg-amber-100 text-amber-700">{corrente.stato}</Badge>
          <span className="ml-auto text-sm text-slate-500">Punteggio: <b className="text-slate-800" data-testid="giro-punteggio-parziale">{punteggioParziale}</b> / {definizione.punteggio_max}</span>
        </div>
        <p className="text-xs text-slate-500">{definizione.note_uso}</p>

        {definizione.items.map(item => (
          <Card key={item.id} data-testid={`giro-item-${item.id}`}>
            <CardContent className="p-3.5">
              <p className="text-sm font-medium text-slate-700 mb-2">{item.testo}</p>
              <div className="flex flex-wrap gap-2">
                {item.opzioni.map(o => (
                  <button key={o.punti} type="button" onClick={() => setRisposte(r => ({ ...r, [item.id]: o.punti }))}
                    className={`px-3 py-2 text-xs rounded-lg border font-medium transition-all ${risposte[item.id] === o.punti
                      ? 'bg-brand text-white border-brand shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:border-brand/50'}`}
                    data-testid={`giro-opt-${item.id}-${o.punti}`}>
                    {o.label} <span className="opacity-60">({o.punti})</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        <Textarea placeholder="Note della scala (facoltative)" value={note} onChange={e => setNote(e.target.value)} className="text-sm" data-testid="giro-note" />

        <Card>
          <CardContent className="p-3.5">
            <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
              <Mic className="w-3.5 h-3.5 text-brand" />Nota di diario clinico (facoltativa, dettabile)
            </p>
            <DictableTextarea rows={3} value={notaDiario} onChange={e => setNotaDiario(e.target.value)}
              placeholder="Osservazioni da lasciare in cartella per questo ospite"
              contesto={`Nota di diario clinico per ${corrente.ospite_nome}`}
              className="text-sm" data-testid="giro-nota-diario" />
          </CardContent>
        </Card>

        <div className="flex items-center justify-between gap-2 pb-10">
          <Button variant="outline" size="sm" disabled={indice === 0} onClick={() => vai(-1)} data-testid="giro-precedente">
            <ArrowLeft className="w-4 h-4 mr-1" />Precedente
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => vai(1)} data-testid="giro-salta">
              <SkipForward className="w-4 h-4 mr-1" />Salta
            </Button>
            <Button size="sm" onClick={salva} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white" data-testid="giro-salva">
              <Check className="w-4 h-4 mr-1" />{salvando ? 'Salvataggio...' : 'Salva e continua'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
